import {
  EnvironmentProviders,
  inject,
  provideEnvironmentInitializer
} from '@angular/core';
import {
  Event,
  NavigationEnd,
  NavigationError,
  Router
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import {
  CHUNK_ERROR_PATTERN,
  extractChunkErrorMessage,
  RELOAD_FLAG
} from './chunk-load-error.handler';

/**
 * Per-target-URL recovery flag prefix. Keyed by URL so a reload loop on
 * one genuinely broken route can't block recovery for a different one.
 */
const NAV_RECOVERY_FLAG_PREFIX = 'velocity:chunk-nav-recovery:';

/**
 * Stale-lazy-chunk navigation recovery (#963).
 *
 * After a deploy lands mid-session, the already-loaded index still
 * references hashed lazy chunks that no longer exist on the static host.
 * The next `loadComponent` navigation (e.g. the tenant-rep property-name
 * click → `/location/:id`) rejects its dynamic import; the router emits
 * `NavigationError`, the URL never changes, and to the user the click
 * just "does nothing". QA's workaround — duplicating the tab — worked
 * precisely because it re-fetched a fresh index.
 *
 * The global `ChunkLoadErrorHandler` already catches the same failure via
 * the unhandled navigation-promise rejection, but it `location.reload()`s
 * the CURRENT url, losing the navigation target, and its session flag is
 * one-shot (never cleared on success) — so the second stale-chunk event in
 * a session was a permanent dead click. This listener owns the navigation
 * case instead:
 *
 * - `location.assign(targetUrl)` re-fetches the fresh index AND lands the
 *   user on the route they actually asked for;
 * - the loop guard is keyed per target URL in sessionStorage and cleared
 *   on any successful navigation, so each new deploy can recover again;
 * - if the flag is already set for that URL (the import failed again right
 *   after a recovery reload — a genuinely broken deploy), it logs and does
 *   nothing instead of reload-looping;
 * - the legacy handler's one-shot reload flag is pre-armed before
 *   navigating away so the in-flight promise rejection reaching
 *   `ChunkLoadErrorHandler` can't fire a competing `location.reload()`.
 *
 * Matches Chrome ("Failed to fetch dynamically imported module"),
 * Firefox ("error loading dynamically imported module") and Safari /
 * webpack ("ChunkLoadError" / "Loading chunk … failed") phrasings via the
 * shared CHUNK_ERROR_PATTERN.
 */
export function provideChunkLoadNavigationRecovery(): EnvironmentProviders {
  return provideEnvironmentInitializer((): void => {
    const router: Router = inject(Router);
    const swUpdate: SwUpdate | null = inject(SwUpdate, { optional: true });

    router.events.subscribe((event: Event): void => {
      if (event instanceof NavigationEnd) {
        clearRecoveryFlags();
        return;
      }
      if (!(event instanceof NavigationError)) {
        return;
      }
      const message: string = extractChunkErrorMessage(event.error);
      if (!CHUNK_ERROR_PATTERN.test(message)) {
        return;
      }
      const targetUrl: string = event.url;
      const flagKey: string = NAV_RECOVERY_FLAG_PREFIX + targetUrl;
      if (sessionStorage.getItem(flagKey)) {
        console.error(
          'ChunkLoadNavigationRecovery: chunk load for',
          targetUrl,
          'failed again after a recovery reload; not reloading again.'
        );
        return;
      }
      sessionStorage.setItem(flagKey, '1');
      // Pre-arm the global ErrorHandler's one-shot flag: the rejected
      // navigation promise is usually unhandled and reaches
      // ChunkLoadErrorHandler moments after this event — without the flag
      // it would issue a competing location.reload() of the CURRENT url
      // that races (and can cancel) the assign below.
      sessionStorage.setItem(RELOAD_FLAG, '1');
      navigateFresh(swUpdate, targetUrl);
    });
  });
}

/**
 * Full-document navigation to the intended target. When the service
 * worker is active the stale chunk may be served from its cache, so the
 * pending version is activated first — same dance as
 * ChunkLoadErrorHandler.recover().
 */
function navigateFresh(swUpdate: SwUpdate | null, targetUrl: string): void {
  const assign = (): void => {
    window.location.assign(targetUrl);
  };
  if (swUpdate?.isEnabled) {
    swUpdate
      .activateUpdate()
      .catch((err: unknown): void => {
        console.error('ChunkLoadNavigationRecovery: activateUpdate failed', err);
      })
      .finally(assign);
    return;
  }
  assign();
}

/**
 * A navigation completed — the app's chunk references are good, so any
 * earlier recovery flags are stale. Clearing them lets the NEXT deploy's
 * stale-chunk event recover too. The legacy one-shot RELOAD_FLAG is
 * cleared as well: it gets pre-armed before every navigateFresh, and
 * leaving it set after a SUCCESSFUL recovery would permanently spend
 * the legacy handler's reload budget — the next non-router chunk
 * failure (a stale import inside an already-loaded page) would surface
 * an error instead of reloading. A completed navigation is proof the
 * current index is healthy, so restoring the one-shot budget here
 * keeps the loop guard intact for the case it exists for: a failure
 * that recurs immediately after a reload, before any navigation lands.
 */
function clearRecoveryFlags(): void {
  const stale: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key: string | null = sessionStorage.key(i);
    if (key && key.startsWith(NAV_RECOVERY_FLAG_PREFIX)) {
      stale.push(key);
    }
  }
  for (const key of stale) {
    sessionStorage.removeItem(key);
  }
  sessionStorage.removeItem(RELOAD_FLAG);
}
